export function eventTransform(data: Record<string, unknown>): Record<string, unknown> {
  // Strip the UI-only size mode field
  const { _team_size_mode: sizeMode, ...step1 } = data;
  let result: Record<string, unknown> = step1;

  // Strip wearables when not enabled
  if (!(result['wearables'] as any)?.enabled) {
    const { wearables: _w, ...noWear } = result;
    result = noWear;
  }

  // Strip measurements for pass_fail
  if (result['scoring_mode'] === 'pass_fail') {
    const { measurements: _m, ...rest } = result;
    result = rest;
  }

  // Auto-fill measurement names from event name (Task 5).
  // Single measurement → event name; multiple → first gets event name, rest get #2, #3…
  if (result['scoring_mode'] === 'measured' && Array.isArray(result['measurements'])) {
    const eventName = (result['name'] as string) ?? '';
    const measurements = result['measurements'] as any[];
    result = {
      ...result,
      measurements: measurements.map((m, i) => ({
        ...m,
        name: measurements.length === 1 || i === 0 ? eventName : `${eventName} #${i + 1}`,
      })),
    };
  }

  // Strip team fields for non-team events
  if (result['participation'] !== 'team') {
    const { team_size: _ts, team_scoring: _tsc, ...rest } = result;
    return rest;
  }

  // Team: keep only the relevant team_size shape
  const ts = result['team_size'] as Record<string, unknown> | undefined;
  if (ts) {
    if (sizeMode === 'range') {
      const { exact: _e, ...rangeTs } = ts;
      result = { ...result, team_size: rangeTs };
    } else {
      const { min: _mn, max: _mx, ...exactTs } = ts;
      result = { ...result, team_size: exactTs };
    }
  }

  return result;
}

export function validateMeasurements(
  value: unknown,
  allValues: Record<string, unknown>,
): string | undefined {
  if ((allValues as any)?.scoring_mode !== 'measured') return undefined;
  if (!Array.isArray(value) || value.length === 0) {
    return 'At least one measurement is required for measured events.';
  }
  return undefined;
}

export function validateTeamSizeExact(
  value: unknown,
  allValues: Record<string, unknown>,
): string | undefined {
  if (allValues['participation'] !== 'team') return undefined;
  if (allValues['_team_size_mode'] === 'range') return undefined;
  const n = Number(value);
  if (!Number.isInteger(n) || n < 2) return 'Must be an integer ≥ 2';
  return undefined;
}

export function validateTeamSizeMin(
  value: unknown,
  allValues: Record<string, unknown>,
): string | undefined {
  if (allValues['participation'] !== 'team') return undefined;
  if (allValues['_team_size_mode'] !== 'range') return undefined;
  const n = Number(value);
  if (!Number.isInteger(n) || n < 2) return 'Must be an integer ≥ 2';
  return undefined;
}

export function validateTeamSizeMax(
  value: unknown,
  allValues: Record<string, unknown>,
): string | undefined {
  if (allValues['participation'] !== 'team') return undefined;
  if (allValues['_team_size_mode'] !== 'range') return undefined;
  const maxVal = Number(value);
  const minVal = Number((allValues['team_size'] as any)?.min);
  if (!Number.isInteger(maxVal)) return 'Must be a valid integer';
  if (maxVal < minVal) return 'Maximum must be ≥ minimum';
  return undefined;
}

// ---------------------------------------------------------------------------
// Time unit scheme helpers (Task 6)
// ---------------------------------------------------------------------------

const FIXED_TIME_ORDER = ['HOURS', 'MINUTES', 'SECONDS', 'TENTHS', 'HUNDREDTHS'] as const;
type TimePart = typeof FIXED_TIME_ORDER[number];

const PART_TO_CODE: Record<TimePart, string> = {
  HOURS: 'HR', MINUTES: 'MIN', SECONDS: 'SEC', TENTHS: 'TENTHS', HUNDREDTHS: 'HUNDREDTHS',
};

const CODE_TO_PART: Record<string, TimePart> = {
  HR: 'HOURS', MIN: 'MINUTES', SEC: 'SECONDS', TENTHS: 'TENTHS', HUNDREDTHS: 'HUNDREDTHS',
};

/**
 * Converts selected toggle parts (any order) to the canonical TIME_* scheme id.
 * E.g. ['MINUTES', 'SECONDS'] → 'TIME_MIN_SEC'
 */
export function togglesToTimeSchemeId(selectedParts: string[]): string {
  const ordered = FIXED_TIME_ORDER.filter(p => selectedParts.includes(p));
  if (ordered.length === 0) return '';
  return 'TIME_' + ordered.map(p => PART_TO_CODE[p]).join('_');
}

/**
 * Parses a TIME_* scheme id back to the set of selected part names.
 * E.g. 'TIME_MIN_SEC' → ['MINUTES', 'SECONDS']
 * Returns [] for unrecognized ids.
 */
export function timeSchemeIdToToggles(id: string): string[] {
  if (!id || !id.startsWith('TIME_')) return [];
  const rawCodes = id.slice(5);
  if (!rawCodes) return [];
  const codes = rawCodes.split('_');
  const parts = codes.map(code => CODE_TO_PART[code]).filter(Boolean);
  // Preserve fixed order
  return FIXED_TIME_ORDER.filter(p => parts.includes(p));
}

const PART_DISPLAY: Record<TimePart, string> = {
  HOURS: 'HH', MINUTES: 'MM', SECONDS: 'SS', TENTHS: 'T', HUNDREDTHS: 'H',
};

/**
 * Returns a human-readable format preview for a set of time parts.
 * E.g. ['MINUTES', 'SECONDS'] → 'MM : SS'
 */
export function timeFormatPreview(selectedParts: string[]): string {
  const ordered = FIXED_TIME_ORDER.filter(p => selectedParts.includes(p));
  const UPPER: readonly string[] = ['HOURS', 'MINUTES', 'SECONDS'];
  const upperParts = ordered.filter(p => UPPER.includes(p));
  const fracParts = ordered.filter(p => !UPPER.includes(p));
  let fmt = upperParts.map(p => PART_DISPLAY[p as TimePart]).join(' : ');
  if (fracParts.length > 0) {
    if (fmt) fmt += ' . ';
    fmt += fracParts.map(p => PART_DISPLAY[p as TimePart]).join('');
  }
  return fmt;
}

export function validateTeamScoring(
  value: unknown,
  allValues: Record<string, unknown>,
): string | undefined {
  if (allValues['participation'] !== 'team') return undefined;
  if (!value) return 'Scoring model is required for team events';
  return undefined;
}

export function validateTeamLeak(values: Record<string, unknown>): Record<string, string> | undefined;
export function validateTeamLeak(
  value: unknown,
  allValues: Record<string, unknown>,
): string | undefined;
export function validateTeamLeak(
  value: unknown,
  allValues?: Record<string, unknown>,
): string | Record<string, string> | undefined {
  const values = allValues ?? value as Record<string, unknown>;
  if (values['participation'] !== 'individual') return undefined;
  if (values['team_scoring'] || values['team_size']) {
    const error = 'Team configuration must be cleared when participation is Individual';
    return allValues ? error : { team_scoring: error };
  }
  return undefined;
}
